/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import { BankAccountVerifyFormFields } from 'forms/settings/payment/BankAccountVerifyForm';
import { Crop } from 'react-image-crop';
import { FileWithPreview, core, safeEnumCompare, safeToNumber } from '@tock/coretypes';
import { dashboard } from 'types';
import { stringify } from '@tock/query-string';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  BookingReleaseReminderConfig,
  BookingReleaseType,
  BusinessPolicies,
  BusinessTax,
  CommunicationPolicy,
  SubscriptionBankAccount,
  SubscriptionPaymentCard,
  VerifyBankAccountRequest,
  BankAccountUpsertRequest,
  BankAccountVerifyRequest,
} = protos;

/**
 * Fetch a business's details
 *
 * @param {number} id The id of the business to fetch.
 * @returns {Object} A redux TockApi action.
 */
export function fetch(id: number): ApiAction {
  if (!_.isNumber(id)) {
    window.LOGGER?.warn('BusinessId is null');
  }
  return {
    TockApi: {
      getProto: `/business/${id}`,
      type: AppConstants.BUSINESS_FETCH,
    },
  };
}

/**
 * Update the given business.
 *
 * Consider adding a FieldMask when working on this endpoint (see https://protobuf.dev/reference/java/api-docs/com/google/protobuf/FieldMask).
 * It is used in many places and has been a source of bugs in the past.
 *
 * @param {Business} business The business to update.
 * @param {string} toastMessage A custom message to put in the toast on success.
 * @returns {Object} A redux TockApi action.
 */
export function update(business: core.Business, toastMessage = 'Business updated'): ApiAction {
  return {
    TockApi: {
      putProto: '/business',
      type: AppConstants.BUSINESS_UPDATE,
      request: business,
      toast: toastMessage,
    },
  };
}

/**
 * Update print chit when seating
 * @returns {Object} A redux TockApi action.
 */
export function updatePrintChitWhenSeating(value: boolean): ApiAction {
  return {
    TockApi: {
      putProto: `/business/details/toggle/print/chit?value=${value}`,
      type: AppConstants.BUSINESS_DETAILS_PRINT_CHIT_WHEN_SEATING_UPDATE,
    },
  };
}

/**
 * Fetches the subscription account information for this business.
 *
 * @returns {Object} A redux TockApi action.
 */
export function fetchPaymentAccount(): ApiAction {
  return {
    TockApi: {
      getProto: '/business/payment/subscription',
      type: AppConstants.BUSINESS_PAYMENT_FETCH,
    },
  };
}

export function updatePaymentInvoiceEmail(paymentAccount: any, invoiceEmail: any): ApiAction {
  const requestPath = '/business/payment/' + (paymentAccount.bankName ? 'bank' : 'card');
  const request = paymentAccount.bankName
    ? SubscriptionBankAccount.fromObject({ invoiceEmail })
    : SubscriptionPaymentCard.fromObject({ invoiceEmail });
  return {
    TockApi: {
      putProto: requestPath,
      request,
      toast: 'Payment account updated',
      type: AppConstants.BUSINESS_PAYMENT_ACCOUNT_UPDATE,
    },
  };
}

export function updatePaymentCard(invoiceEmail: any, paymentCardToken: any): ApiAction {
  const request = SubscriptionPaymentCard.fromObject({
    invoiceEmail,
    paymentCardToken,
  });
  return {
    TockApi: {
      putProto: '/business/payment/card',
      request,
      toast: 'Payment account updated',
      type: AppConstants.BUSINESS_PAYMENT_ACCOUNT_UPDATE,
    },
  };
}

export function updatePaymentBankAccount(invoiceEmail: any, bankAccount: any): ApiAction {
  const request = SubscriptionBankAccount.fromObject({
    invoiceEmail,
    bankAccount,
  });
  return {
    TockApi: {
      putProto: '/business/payment/bank',
      request,
      toast: 'Payment account updated',
      type: AppConstants.BUSINESS_PAYMENT_ACCOUNT_UPDATE,
    },
  };
}

export function switchToBankAccount(): ApiAction {
  const request = SubscriptionBankAccount.fromObject({});
  return {
    TockApi: {
      putProto: '/business/payment/subscription/bank',
      request,
      type: AppConstants.BUSINESS_PAYMENT_SWITCH_TO_BRAINTREE,
    },
  };
}

export function fetchTockBankAccount(): ApiAction {
  return {
    TockApi: {
      getProto: '/business/bank/account',
      type: AppConstants.TOCK_BANK_ACCOUNT_FETCH,
    },
  };
}

export function upsertTockBankAccount(bankAccount: dashboard.BankAccountUpsertRequest): ApiAction {
  const request = BankAccountUpsertRequest.fromObject(bankAccount);
  return {
    TockApi: {
      postProto: '/business/bank/account',
      request,
      toast: 'Bank account added',
      type: AppConstants.TOCK_BANK_ACCOUNT_UPDATE,
    },
  };
}

export function verifyTockBankAccount(depositedAmounts: BankAccountVerifyFormFields): ApiAction {
  const { deposit1, deposit2 } = depositedAmounts;
  const request = BankAccountVerifyRequest.fromObject({
    depositedCents_1: deposit1,

    depositedCents_2: deposit2,
  });
  return {
    TockApi: {
      postProto: '/business/bank/account/verify',
      request,
      toast: 'Bank account verified',
      type: AppConstants.TOCK_BANK_ACCOUNT_VERIFY,
    },
  };
}

export function verifyBankAccount(depositedAmounts: BankAccountVerifyFormFields): ApiAction {
  const { deposit1, deposit2 } = depositedAmounts;
  const request = VerifyBankAccountRequest.fromObject({
    depositAmount: [deposit1, deposit2],
  });
  return {
    TockApi: {
      putProto: '/business/payment/bank/verify',
      request,
      toast: 'Bank account verified',
      type: AppConstants.BUSINESS_PAYMENT_VERIFY_BANK,
    },
  };
}

export function setVerifiedBankAccount(verifiedBankAccount: any): ApiAction {
  const request = SubscriptionBankAccount.fromObject({
    verifiedBankAccount,
  });
  return {
    TockApi: {
      putProto: '/business/payment/bank',
      request,
      toast: 'Payment account updated',
      type: AppConstants.BUSINESS_PAYMENT_ACCOUNT_UPDATE,
    },
  };
}

export function fetchBookingReleases(): ApiAction {
  return {
    TockApi: {
      getListProto: '/booking/release',
      type: AppConstants.BOOKING_RELEASE_FETCH,
    },
  };
}

export function addBookingRelease(request: core.BookingRelease): ApiAction {
  return {
    TockApi: {
      postProto: '/booking/release',
      request,
      type: AppConstants.BOOKING_RELEASE_CREATE,
      toast: safeEnumCompare(request.type, BookingReleaseType, BookingReleaseType.SCHEDULED_RELEASE)
        ? 'Release scheduled'
        : 'Availability window updated',
    },
  };
}

export function setReminderForBookingRelease(release: any, reminder: any): ApiAction {
  const request = BookingReleaseReminderConfig.fromObject({
    bookingReleaseId: safeToNumber(release.id),
    reminderEmailContent: reminder.reminderEmailContent,
    reminderEmailNumHoursBefore: _.toNumber(reminder.reminderEmailNumHoursBefore),
  });
  return {
    TockApi: {
      putProto: '/booking/release/reminder',
      request,
      type: AppConstants.NULL,
      toast: 'Reminder email scheduled',
    },
  };
}

export function deleteReminderForBookingRelease(release: any): ApiAction {
  return {
    TockApi: {
      deleteProto: `/booking/release/reminder/${safeToNumber(release.id)}`,
      type: AppConstants.NULL,
      toast: 'Reminder email deleted',
    },
  };
}

export function archiveBookingRelease(request: any): ApiAction {
  return {
    TockApi: {
      postProto: '/booking/release/archive',
      request,
      type: AppConstants.NULL,
      toast: 'Release canceled',
    },
  };
}

export function updateBusinessImage(
  file: FileWithPreview,
  imageUse: 'logo' | 'hero',
  crop: Crop
): ApiAction {
  const { x, y, width, height } = crop;
  return {
    TockApi: {
      postFile: `/business/upload/image?imageUse=${imageUse}&x=${x}&y=${y}&width=${width}&height=${height}`,
      request: file,
      type: AppConstants.BUSINESS_IMAGE_UPDATE,
      toast: 'Image uploaded successfully',
    },
  };
}

export function fetchDefaultCommunicationPolicy(): ApiAction {
  return {
    TockApi: {
      getProto: '/business/communication',
      type: AppConstants.BUSINESS_COMMUNICATION_POLICY_FETCH,
    },
  };
}

/**
 * Fetch all the global business taxes for a business.
 */
export function fetchBusinessTaxes(): ApiAction {
  return {
    TockApi: {
      getListProto: '/business/tax',
      type: AppConstants.BUSINESS_TAX_FETCH,
    },
  };
}

/**
 * Delete the tax for a business. This deletes it from all experiences
 * using the tax.
 */
export function archiveBusinessTax(id: number, name: string): ApiAction {
  return {
    TockApi: {
      putProto: `/business/tax/archive/${id}`,
      type: AppConstants.BUSINESS_TAX_ARCHIVE,
      toast: `Deleted tax named: ${name}`,
    },
  };
}

/**
 * Upsert a business tax.
 * @param businessTax The business tax to add.
 */
export function upsertBusinessTax(businessTax: core.IBusinessTax): ApiAction {
  return {
    TockApi: {
      postProto: '/business/tax',
      request: BusinessTax.fromObject(businessTax),
      type: AppConstants.BUSINESS_TAX_UPSERT,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      toast: `${businessTax.name} (${businessTax.percent!.value}%) saved`,
    },
  };
}

export function updateDefaultCommunicationPolicy(policy: core.ICommunicationPolicy): ApiAction {
  const request = CommunicationPolicy.fromObject(policy);
  return {
    TockApi: {
      putProto: '/business/communication',
      request,
      type: AppConstants.BUSINESS_COMMUNICATION_POLICY_UPDATE,
      toast: 'Communication policy updated',
    },
  };
}

export function fetchDefaultBusinessPolicies(): ApiAction {
  return {
    TockApi: {
      getProto: '/business/policies',
      type: AppConstants.BUSINESS_POLICIES_FETCH,
    },
  };
}

export function updateDefaultBusinessPolicies(policies: core.IBusinessPolicies): ApiAction {
  const request = BusinessPolicies.fromObject(policies);
  return {
    TockApi: {
      putProto: '/business/policies',
      request,
      type: AppConstants.BUSINESS_POLICIES_UPDATE,
      toast: 'Business policies updated',
    },
  };
}

export function updatePlan(planName?: string) {
  const plan = planName ? planName : 'unspecified';
  return {
    TockApi: {
      getProto: `/business/plan/update?${stringify({ plan })}`,
      type: AppConstants.BUSINESS_PLAN_UPDATE,
    },
  };
}

export function reorderAccolades(reorderedAccolades: core.IBusinessAccolade[]) {
  return {
    type: AppConstants.BUSINESS_ACCOLADE_REORDER,
    payload: {
      reorderedAccolades,
    },
  };
}

export function getConsumerWidgetToken() {
  return {
    TockApi: {
      getProto: `/widget/builder/token`,
      type: AppConstants.BUSINESS_CONSUMER_WIDGET_TOKEN_FETCH,
    },
  };
}
